/**
 * TravelTripInfoPanel — trip-level context the traveler sets (party +
 * relationship, interests, budget, pace, dietary, notes). Mostly user-driven;
 * the values feed the AI so budgets, packing, reservations, and itinerary
 * suggestions match the trip's vibe (e.g. a couple → romantic dining;
 * coworkers → casual/professional).
 */

import { useEffect, useState } from 'react';
import { Users, Check, Loader2, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { useTravelTripInfo } from '../hooks';
import type { TravelBudgetLevel, TravelPace } from '../api';

const RELATIONSHIP_PRESETS = [
  'Solo',
  'Couple',
  'Family',
  'Family with kids',
  'Friends',
  'Coworkers / business',
  'Group',
];

const INTEREST_PRESETS = [
  'Food & dining',
  'History',
  'Art & museums',
  'Architecture',
  'Nightlife',
  'Outdoors & hiking',
  'Beaches',
  'Shopping',
  'Local culture',
  'Photography',
  'Live music',
  'Adventure',
  'Relaxation & spa',
  'Family-friendly',
  'Sports',
  'Wine & breweries',
];

const DIETARY_PRESETS = [
  'Vegetarian',
  'Vegan',
  'Gluten-free',
  'Dairy-free',
  'Nut allergy',
  'Halal',
  'Kosher',
  'Pescatarian',
];

const BUDGET_LEVELS: Array<{ value: TravelBudgetLevel; label: string }> = [
  { value: 'budget', label: 'Budget' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'luxury', label: 'Luxury' },
];

const PACES: Array<{ value: TravelPace; label: string }> = [
  { value: 'relaxed', label: 'Relaxed' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'packed', label: 'Packed' },
];

function TagEditor({
  selected,
  presets,
  onChange,
  placeholder,
}: {
  selected: string[];
  presets: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}) {
  const [draft, setDraft] = useState('');
  const toggle = (t: string) =>
    selected.includes(t) ? onChange(selected.filter((x) => x !== t)) : onChange([...selected, t]);
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (!selected.some((x) => x.toLowerCase() === t.toLowerCase())) onChange([...selected, t]);
    setDraft('');
  };
  // Presets not already chosen, so the chip row doesn't double up with selected.
  const remainingPresets = presets.filter((p) => !selected.includes(p));

  return (
    <div className="space-y-1.5">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => toggle(t)}
              className="inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 bg-primary/15 text-primary"
            >
              {t} <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}
      {remainingPresets.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {remainingPresets.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => toggle(t)}
              className="text-xs rounded-full px-2 py-0.5 border border-border text-muted-foreground hover:bg-muted"
            >
              + {t}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          className="h-8 text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button size="sm" variant="secondary" onClick={add} disabled={!draft.trim()}>
          Add
        </Button>
      </div>
    </div>
  );
}

export function TravelTripInfoPanel({
  tripId,
  open,
  onOpenChange,
}: {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { info, loading, update, isSaving } = useTravelTripInfo(tripId);

  const [partySize, setPartySize] = useState('');
  const [relationship, setRelationship] = useState<string[]>([]);
  const [interests, setInterests] = useState<string[]>([]);
  const [budgetLevel, setBudgetLevel] = useState<TravelBudgetLevel | ''>('');
  const [hardBudget, setHardBudget] = useState('');
  const [pace, setPace] = useState<TravelPace | ''>('');
  const [dietary, setDietary] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  // Hydrate the form when the row loads / the trip changes.
  useEffect(() => {
    setPartySize(info?.partySize != null ? String(info.partySize) : '');
    setRelationship(info?.relationship ?? []);
    setInterests(info?.interests ?? []);
    setBudgetLevel(info?.budgetLevel ?? '');
    setHardBudget(info?.hardBudgetCents != null ? (info.hardBudgetCents / 100).toFixed(0) : '');
    setPace(info?.pace ?? '');
    setDietary(info?.dietary ?? []);
    setNotes(info?.notes ?? '');
  }, [info, tripId]);

  const handleSave = async () => {
    const sizeNum = parseInt(partySize, 10);
    const capNum = parseFloat(hardBudget);
    try {
      await update({
        partySize: partySize.trim() === '' ? null : Number.isFinite(sizeNum) ? sizeNum : null,
        relationship,
        interests,
        budgetLevel: budgetLevel === '' ? null : budgetLevel,
        hardBudgetCents:
          hardBudget.trim() === ''
            ? null
            : Number.isFinite(capNum)
              ? Math.round(capNum * 100)
              : null,
        pace: pace === '' ? null : pace,
        dietary,
        notes: notes.trim() || null,
      });
      toast({ title: 'Trip info saved', description: 'The AI will use this for suggestions.' });
    } catch (err) {
      toast({
        title: 'Could not save',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" /> Trip info
          </SheetTitle>
          <SheetDescription>
            Set the context for this trip. The AI uses it to tailor dining, activities, packing, and
            budget — e.g. a couple gets more romantic picks; coworkers get casual/professional ones.
          </SheetDescription>
        </SheetHeader>

        {loading && !info ? (
          <div className="mt-4 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="mt-4 space-y-5">
            {/* Travelers */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Travelers
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={99}
                  value={partySize}
                  onChange={(e) => setPartySize(e.target.value)}
                  placeholder="# people"
                  className="w-24 h-8 text-sm"
                />
                <span className="text-xs text-muted-foreground">who's going</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Pick all that apply — the AI blends them (and may ask later if it&apos;s unclear).
              </p>
              <TagEditor
                selected={relationship}
                presets={RELATIONSHIP_PRESETS}
                onChange={setRelationship}
                placeholder="Add your own…"
              />
            </div>

            {/* Interests */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Interests
              </Label>
              <TagEditor
                selected={interests}
                presets={INTEREST_PRESETS}
                onChange={setInterests}
                placeholder="Add your own interest…"
              />
            </div>

            {/* Budget */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Budget
              </Label>
              <div className="flex flex-wrap gap-1">
                {BUDGET_LEVELS.map((b) => (
                  <button
                    key={b.value}
                    type="button"
                    onClick={() => setBudgetLevel(budgetLevel === b.value ? '' : b.value)}
                    className={`text-xs rounded-full px-2 py-0.5 border ${
                      budgetLevel === b.value
                        ? 'border-primary bg-primary/15 text-primary'
                        : 'border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Hard cap (optional)</span>
                <Input
                  type="number"
                  min={0}
                  step="50"
                  value={hardBudget}
                  onChange={(e) => setHardBudget(e.target.value)}
                  placeholder="$ total"
                  className="w-28 h-8 text-sm"
                />
              </div>
            </div>

            {/* Pace */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Pace</Label>
              <div className="flex flex-wrap gap-1">
                {PACES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPace(pace === p.value ? '' : p.value)}
                    className={`text-xs rounded-full px-2 py-0.5 border ${
                      pace === p.value
                        ? 'border-primary bg-primary/15 text-primary'
                        : 'border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dietary */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Dietary needs
              </Label>
              <TagEditor
                selected={dietary}
                presets={DIETARY_PRESETS}
                onChange={setDietary}
                placeholder="Add a dietary need…"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Anything else for the AI
              </Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. celebrating an anniversary; want one splurge dinner; avoid early mornings"
                rows={3}
                className="text-sm"
              />
            </div>

            <Button onClick={() => void handleSave()} disabled={isSaving} className="w-full">
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" /> Save trip info
                </>
              )}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
